import React, { useState, useEffect } from 'react';
import './MentorAuth.css';

function MentorAuth({ socket, onAuthSuccess }) {
  // States
  const [step, setStep] = useState('MENTORS_LIST'); // MENTORS_LIST, CODE_INPUT
  const [mentors, setMentors] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const codeInputs = React.useRef([]);

  // טעינת רשימת מנטורים בטעינה ראשונה
  useEffect(() => {
    console.log('🔄 מבקש רשימת מנטורים');
    socket.emit('get_mentors');

    const handleMentorsList = (data) => {
      console.log('📋 קיבלתי רשימת מנטורים:', data);
      setMentors(data || []);
    };

    socket.on('mentors_list', handleMentorsList);

    return () => {
      socket.off('mentors_list', handleMentorsList);
    };
  }, [socket]);

  // האזנה לאירועי מנטור חדש
  useEffect(() => {
    const handleMentorCreated = (data) => {
      console.log('✅ מנטור נוצר:', data);
      if (data.success) {
        setSelectedMentor({ id: data.mentorId, name: data.name, code_hash: null });
        setStep('CODE_INPUT');
        setError('');
      } else {
        setError('שגיאה ביצירת מנטור');
      }
      setLoading(false);
    };

    socket.on('mentor_created', handleMentorCreated);

    return () => {
      socket.off('mentor_created', handleMentorCreated);
    };
  }, [socket]);

  // האזנה לאירועי אימות קוד
  useEffect(() => {
    const handleCodeSet = (data) => {
      console.log('📝 תוצאת הגדרת קוד:', data);
      if (data.success) {
        localStorage.setItem('currentMentorId', selectedMentor.id);
        localStorage.setItem('currentMentorName', selectedMentor.name);
        connectToWhatsApp();
      } else {
        setError('שגיאה בהגדרת קוד');
        setLoading(false);
      }
    };

    const handleCodeVerified = (data) => {
      console.log('🔐 תוצאת אימות קוד:', data);
      if (data.success) {
        localStorage.setItem('currentMentorId', selectedMentor.id);
        localStorage.setItem('currentMentorName', selectedMentor.name);
        connectToWhatsApp();
      } else {
        setError('קוד שגוי');
        setCode(['', '', '', '', '', '']);
        if (codeInputs.current[0]) {
          codeInputs.current[0].focus();
        }
        setLoading(false);
      }
    };

    socket.on('code_set_result', handleCodeSet);
    socket.on('code_verified', handleCodeVerified);

    return () => {
      socket.off('code_set_result', handleCodeSet);
      socket.off('code_verified', handleCodeVerified);
    };
  }, [socket, selectedMentor]);

  // האזנה לאירועי WhatsApp
  useEffect(() => {
    const handleQR = (data) => {
      console.log('📱 קיבלתי QR Code:', data.qr);
      setQrCode(data.qr);
      setShowQrModal(true);
      setLoading(false);
    };

    const handleReady = (data) => {
      console.log('✅ WhatsApp מוכן!');
      setShowQrModal(false);
      setQrCode(null);
      setLoading(false);
      onAuthSuccess(selectedMentor?.id || data.mentorId);
    };

    socket.on('qr', handleQR);
    socket.on('ready', handleReady);

    return () => {
      socket.off('qr', handleQR);
      socket.off('ready', handleReady);
    };
  }, [socket, selectedMentor, onAuthSuccess]);

  // פונקציה לחיבור WhatsApp
  const connectToWhatsApp = () => {
    console.log('🔌 מתחבר ל-WhatsApp עבור מנטור:', selectedMentor.id);
    setLoading(true);
    socket.emit('mentor_login', { mentorId: selectedMentor.id });
  };

  // פונקציה ליצירת מנטור חדש
  const handleCreateMentor = () => {
    const name = prompt('הזן שם למנטור החדש:');
    if (!name || !name.trim()) return;

    setLoading(true);
    setError('');
    socket.emit('create_mentor', { name: name.trim() });
  };

  // פונקציה לבחירת מנטור
  const handleSelectMentor = (mentor) => {
    console.log('👤 נבחר מנטור:', mentor);
    setSelectedMentor(mentor);
    setStep('CODE_INPUT');
    setError('');
  };

  // פונקציה לשינוי קוד
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      codeInputs.current[index + 1]?.focus();
    }

    setError('');
  };

  // פונקציה למקש Backspace
  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputs.current[index - 1]?.focus();
    }
  };

  // פונקציה להדבקה
  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
    setCode(newCode);

    const lastFilledIndex = Math.min(pastedData.length, 5);
    codeInputs.current[lastFilledIndex]?.focus();
  };

  // פונקציה לאישור קוד
  const handleSubmitCode = () => {
    const codeString = code.join('');

    if (codeString.length !== 6) {
      setError('יש להזין 6 ספרות');
      return;
    }

    setLoading(true);
    setError('');

    if (!selectedMentor.code_hash) {
      console.log('📝 מגדיר קוד חדש למנטור');
      socket.emit('set_mentor_code', {
        mentorId: selectedMentor.id,
        code: codeString
      });
    } else {
      console.log('🔐 מאמת קוד למנטור');
      socket.emit('verify_mentor_code', {
        mentorId: selectedMentor.id,
        code: codeString
      });
    }
  };

  // פונקציה לחזרה
  const handleBack = () => {
    setStep('MENTORS_LIST');
    setSelectedMentor(null);
    setCode(['', '', '', '', '', '']);
    setError('');
    setQrCode(null);
    setShowQrModal(false);
    setLoading(false);
    socket.emit('get_mentors');
  };

  // ========== מסכים ==========

  // מסך 1: רשימת מנטורים
  if (step === 'MENTORS_LIST') {
    return (
      <div className="mentor-auth-container">
        <div className="mentor-auth-card">
          <div className="mentor-auth-header">
            <img src="/logoo.png" alt="Logo" className="mentor-auth-logo" />
            <h1>Dark Crypto WhatsApp</h1>
            <p>בחר מנטור כדי להתחבר</p>
          </div>

          <div className="mentors-list">
            {mentors.length === 0 ? (
              <div className="no-mentors">
                <p>אין מנטורים במערכת</p>
                <p className="hint">לחץ על "הוסף מנטור חדש" להתחיל</p>
              </div>
            ) : (
              mentors.map(mentor => (
                <div
                  key={mentor.id}
                  className="mentor-card"
                  onClick={() => handleSelectMentor(mentor)}
                >
                  <div className="mentor-avatar">
                    {mentor.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="mentor-info">
                    <h3>{mentor.name}</h3>
                    {mentor.last_login && (
                      <p className="last-login">
                        כניסה אחרונה: {new Date(mentor.last_login * 1000).toLocaleDateString('he-IL')}
                      </p>
                    )}
                  </div>
                  <svg className="arrow-icon" viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                  </svg>
                </div>
              ))
            )}
          </div>

          <button className="create-mentor-btn" onClick={handleCreateMentor} disabled={loading}>
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
            הוסף מנטור חדש
          </button>
        </div>
      </div>
    );
  }

  // מסך 2: הזנת קוד
  if (step === 'CODE_INPUT') {
    const isNewMentor = !selectedMentor?.code_hash;

    return (
      <>
        <div className="mentor-auth-container">
          <div className="mentor-auth-card code-card">
            <button className="back-btn" onClick={handleBack}>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </button>

            <div className="mentor-auth-header">
              <div className="mentor-avatar-large">
                {selectedMentor?.name?.charAt(0).toUpperCase()}
              </div>
              <h2>{selectedMentor?.name}</h2>
              <p>{isNewMentor ? 'הגדר קוד 6 ספרות' : 'הזן את הקוד שלך'}</p>
            </div>

            <div className="code-input-container">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => codeInputs.current[index] = el}
                  type={isNewMentor ? 'text' : 'password'}
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  onPaste={index === 0 ? handleCodePaste : undefined}
                  className="code-digit"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              className="submit-code-btn"
              onClick={handleSubmitCode}
              disabled={loading || code.join('').length !== 6}
            >
              {loading ? (isNewMentor ? 'מגדיר...' : 'מאמת...') : 'המשך'}
            </button>

            {isNewMentor && (
              <div className="code-hint">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                קוד זה ישמש אותך להתחברות בפעמים הבאות
              </div>
            )}
          </div>
        </div>

        {/* QR Modal */}
        {showQrModal && (
          <div className="qr-modal-overlay">
            <div className="qr-modal">
              <div className="qr-modal-header">
                <h3>סרוק את ה-QR Code</h3>
                <p>הקוד מתחדש אוטומטית כל 20 שניות</p>
              </div>

              <div className="qr-modal-body">
                {qrCode ? (
                  <>
                    <img
                      src={qrCode}
                      alt="QR Code"
                      className="qr-modal-image"
                      key={qrCode}
                      onError={() => console.error('❌ שגיאה בטעינת QR')}
                      onLoad={() => console.log('✅ QR נטען בהצלחה')}
                    />
                    <div className="qr-modal-instructions">
                      <ol>
                        <li>פתח WhatsApp בטלפון</li>
                        <li>לחץ על <strong>תפריט (⋮)</strong></li>
                        <li>בחר <strong>מכשירים מקושרים</strong></li>
                        <li>לחץ על <strong>קשר מכשיר</strong></li>
                        <li>סרוק את הקוד</li>
                      </ol>
                    </div>
                  </>
                ) : (
                  <div className="qr-modal-loading">
                    <div className="spinner"></div>
                    <p>ממתין ל-QR Code...</p>
                  </div>
                )}
              </div>

              <button
                className="qr-modal-close"
                onClick={() => {
                  setShowQrModal(false);
                  handleBack();
                }}
              >
                ביטול
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return null;
}

export default MentorAuth;
